import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Sheet } from "@/components/Sheet";
import { type Ask, DialogContext, type Dialogs } from "@/components/dialogs";

interface Open extends Ask {
  single: boolean;
  resolve: (answer: boolean) => void;
}

/**
 * Asks questions in the app's own sheet rather than the browser's pop-ups, which look different
 * on every device and which some embedded browsers suppress (answering "Cancel" without asking).
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<Open>();
  const [typed, setTyped] = useState("");
  const answered = useRef(false);

  const ask = useCallback(
    (question: Ask, single: boolean) =>
      new Promise<boolean>((resolve) => {
        answered.current = false;
        setTyped("");
        setOpen({ ...question, single, resolve });
      }),
    [],
  );

  const dialogs = useMemo<Dialogs>(
    () => ({
      confirm: (question) => ask(question, false),
      notify: (question) => ask(question, true).then(() => undefined),
    }),
    [ask],
  );

  function answer(yes: boolean) {
    if (!open || answered.current) return;
    answered.current = true;
    open.resolve(yes);
    setOpen(undefined);
  }

  const blocked = open?.typeToConfirm !== undefined && typed.trim() !== open.typeToConfirm;

  return (
    <DialogContext.Provider value={dialogs}>
      {children}
      <Sheet open={open !== undefined} title={open?.title ?? ""} onClose={() => answer(false)}>
        {open && (
          <form
            className="stack confirm-dialog"
            onSubmit={(event) => {
              event.preventDefault();
              if (!blocked) answer(true);
            }}
          >
            {open.message?.split("\n\n").map((paragraph) => (
              <p key={paragraph} className="muted">
                {paragraph}
              </p>
            ))}
            {open.typeToConfirm !== undefined && (
              <label>
                To confirm, type {open.typeToConfirm}
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus
                />
              </label>
            )}
            <div className="confirm-actions">
              {!open.single && (
                <button type="button" className="btn secondary" onClick={() => answer(false)}>
                  {open.cancelLabel ?? "Cancel"}
                </button>
              )}
              <button
                className={open.danger ? "btn danger" : "btn"}
                disabled={blocked}
                autoFocus={open.typeToConfirm === undefined}
              >
                {open.confirmLabel ?? "OK"}
              </button>
            </div>
          </form>
        )}
      </Sheet>
    </DialogContext.Provider>
  );
}
